'use strict';

/**
 * Banner Carousel Configuration Utility
 * Provides reusable Slick carousel configurations and helpers for hero/banner components
 *
 * @module utils/bannerCarouselConfig
 */

const BannerCarouselConfig = {
    /**
     * Get hero banner carousel configuration
     * @param {Object} options - Custom options
     * @param {jQuery} options.prevArrow - Previous arrow element
     * @param {jQuery} options.nextArrow - Next arrow element
     * @param {boolean} options.autoRotate - Enable auto-rotate
     * @param {number} options.rotateInterval - Auto-rotate interval in ms
     * @param {boolean} options.pauseOnHover - Pause on hover
     * @param {boolean} [options.showArrows] - Enable prev/next arrows (default: true)
     * @param {boolean} [options.showDots] - Enable pagination dots (default: false)
     * @param {function} options.beforeChange - Custom beforeChange callback
     * @param {function} options.afterChange - Custom afterChange callback
     * @returns {Object} Slick configuration object
     */
    getHeroBannerConfig: function (options) {
        var configOptions = options || {};

        return {
            autoplay: configOptions.autoRotate || false,
            autoplaySpeed: configOptions.rotateInterval || 5000,
            pauseOnHover: configOptions.pauseOnHover !== false,
            arrows: configOptions.showArrows !== false,
            prevArrow: configOptions.prevArrow,
            nextArrow: configOptions.nextArrow,
            infinite: true,
            speed: 800,
            slidesToShow: 1,
            slidesToScroll: 1,
            fade: true,
            cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
            accessibility: true,
            pauseOnFocus: true,
            adaptiveHeight: false,
            dots: configOptions.showDots === true,
            swipe: true,
            touchMove: true,
            draggable: true,

            beforeChange: configOptions.beforeChange || function (event, slick, _currentSlide, _nextSlide) {
                $(slick.$slides).removeClass('animate-content');
            },

            afterChange: configOptions.afterChange || null
        };
    },

    /**
     * Trigger slide animation for current active slide
     * @param {jQuery} $slidesContainer - Slick container
     * @param {string} activeSlideClass - Class to find active slide (default: 'carousel-slide')
     * @param {number} delay - Delay before animation starts in ms (default: 50)
     */
    triggerSlideAnimation: function ($slidesContainer, activeSlideClass, delay) {
        var slideClass = activeSlideClass || 'carousel-slide';
        var animationDelay = delay || 50;

        setTimeout(function () {
            var $currentSlide = $slidesContainer.find('.slick-active.' + slideClass);

            // Remove animation class
            $currentSlide.removeClass('animate-content');

            // Force reflow to restart animation
            if ($currentSlide[0]) {
                $currentSlide[0].getBoundingClientRect().width; // eslint-disable-line no-unused-expressions
            }

            // Re-add animation class
            requestAnimationFrame(function () {
                $currentSlide.addClass('animate-content');
            });
        }, animationDelay);
    },

    /**
     * Initialize first slide animation
     * @param {jQuery} $slidesContainer - Slick container
     * @param {string} activeSlideClass - Class to find active slide (default: 'carousel-slide')
     */
    initFirstSlideAnimation: function ($slidesContainer, activeSlideClass) {
        var slideClass = activeSlideClass || 'carousel-slide';

        var $firstSlide = $slidesContainer.find('.slick-active.' + slideClass);

        if ($firstSlide.length > 0) {
            $firstSlide.removeClass('animate-content');
            $firstSlide[0].getBoundingClientRect().width; // eslint-disable-line no-unused-expressions

            requestAnimationFrame(function () {
                $firstSlide.addClass('animate-content');
            });
        }
    },

    /**
     * Setup arrow click handlers with animation
     * @param {jQuery} $prevArrow - Previous arrow element
     * @param {jQuery} $nextArrow - Next arrow element
     * @param {jQuery} $slidesContainer - Slick container
     * @param {string} activeSlideClass - Class to find active slide (default: 'carousel-slide')
     */
    setupArrowHandlers: function ($prevArrow, $nextArrow, $slidesContainer, activeSlideClass) {
        var self = this;
        var slideClass = activeSlideClass || 'carousel-slide';

        $prevArrow.off('click').on('click', function (e) {
            e.preventDefault();
            e.stopPropagation();

            $slidesContainer.find('.' + slideClass).removeClass('animate-content');
            $slidesContainer.slick('slickPrev');

            self.triggerSlideAnimation($slidesContainer, slideClass);
        });

        $nextArrow.off('click').on('click', function (e) {
            e.preventDefault();
            e.stopPropagation();

            $slidesContainer.find('.' + slideClass).removeClass('animate-content');
            $slidesContainer.slick('slickNext');

            self.triggerSlideAnimation($slidesContainer, slideClass);
        });
    },

    /**
     * Setup swipe animation handler
     * @param {jQuery} $slidesContainer - Slick container
     * @param {string} activeSlideClass - Class to find active slide (default: 'carousel-slide')
     */
    setupSwipeHandler: function ($slidesContainer, activeSlideClass) {
        var self = this;
        var slideClass = activeSlideClass || 'carousel-slide';

        $slidesContainer.on('swipe', function (_event, _slick, _direction) {
            $slidesContainer.find('.' + slideClass).removeClass('animate-content');
            self.triggerSlideAnimation($slidesContainer, slideClass);
        });
    },

    /**
     * Setup auto-rotate animation detection
     * @param {jQuery} $slidesContainer - Slick container
     * @param {jQuery} $prevArrow - Previous arrow element
     * @param {jQuery} $nextArrow - Next arrow element
     * @param {string} activeSlideClass - Class to find active slide (default: 'carousel-slide')
     */
    setupAutoRotateDetection: function ($slidesContainer, $prevArrow, $nextArrow, activeSlideClass) {
        var self = this;
        var slideClass = activeSlideClass || 'carousel-slide';

        var isUserInteraction = false;
        var lastAutoSlideIndex = $slidesContainer.slick('slickCurrentSlide');

        // Mark user interactions
        $prevArrow.on('click', function () {
            isUserInteraction = true;
        });

        $nextArrow.on('click', function () {
            isUserInteraction = true;
        });

        $slidesContainer.on('swipe', function () {
            isUserInteraction = true;
        });

        // Monitor for auto-rotate
        setInterval(function () {
            var currentSlideIndex = $slidesContainer.slick('slickCurrentSlide');

            // If slide changed and NOT from user interaction = auto-rotate
            if (currentSlideIndex !== lastAutoSlideIndex && !isUserInteraction) {
                $slidesContainer.find('.' + slideClass).removeClass('animate-content');
                self.triggerSlideAnimation($slidesContainer, slideClass);
            }

            lastAutoSlideIndex = currentSlideIndex;
            isUserInteraction = false;
        }, 100);
    },

    /**
     * Setup visibility change handler for pausing carousel
     * @param {jQuery} $slidesContainer - Slick container
     */
    setupVisibilityHandler: function ($slidesContainer) {
        var hidden; var
            visibilityChange;

        if (typeof document.hidden !== 'undefined') {
            hidden = 'hidden';
            visibilityChange = 'visibilitychange';
        } else if (typeof document.msHidden !== 'undefined') {
            hidden = 'msHidden';
            visibilityChange = 'msvisibilitychange';
        } else if (typeof document.webkitHidden !== 'undefined') {
            hidden = 'webkitHidden';
            visibilityChange = 'webkitvisibilitychange';
        }

        if (typeof visibilityChange !== 'undefined') {
            document.addEventListener(visibilityChange, function () {
                if (document[hidden]) {
                    $slidesContainer.slick('slickPause');
                } else {
                    $slidesContainer.slick('slickPlay');
                }
            }, false);
        }
    },

    /**
     * Setup keyboard navigation
     * @param {jQuery} $carousel - Main carousel container element
     * @param {jQuery} $slidesContainer - Slick container
     */
    setupKeyboardNavigation: function ($carousel, $slidesContainer) {
        $carousel.on('keydown', function (e) {
            if (e.key === 'ArrowLeft' || e.keyCode === 37) {
                e.preventDefault();
                $slidesContainer.slick('slickPrev');
            } else if (e.key === 'ArrowRight' || e.keyCode === 39) {
                e.preventDefault();
                $slidesContainer.slick('slickNext');
            }
        });

        $carousel.attr('tabindex', '0');
    }
};

// Export for use in other modules (if using module bundler) or make globally available
module.exports = BannerCarouselConfig;

